import { Show, Table, TableProps, Typography, notification } from '@pankod/refine-antd';
import { IResourceComponentsProps } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import { Search } from 'components';
import { CustomDatePicker } from 'components/date-picker/CustomDatePicker';
import { ESourceTransactionType } from 'enums';
import { useQuery } from 'hooks';
import { IInventoryHistory } from 'interfaces';
import { useCallback, useEffect, useState } from 'react';
import { calculateInventoryHistory, getInventoryHistories } from 'services/inventory';
import { getVariant } from 'services/variant';
import { getWarehouse } from 'services/warehouse';
import { formatCurrency } from 'utilities/shared';
import styles from './style.module.css';
import MetaData from 'utilities/metadata';

const { Text } = Typography;

interface IOptionGroup {
  value: string;
  label: string | React.ReactNode;
}

type SearchKeywords = {
  name: string[];
  orderType: string[];
};

export const getDateRange = () => {
  const yearStartingDate = new Date(2022, 0, 1, 0, 0, 0);

  const currentDate = new Date();
  const presentDate = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate(),
    23,
    59,
    59
  );

  return [yearStartingDate, presentDate] as [Date, Date];
};

export const InventoryShow: React.FC<IResourceComponentsProps> = () => {
  const [inventoryHistory, setInventoryHistory] = useState<IInventoryHistory[]>([]);
  const [totalStockCount, setTotalStockCount] = useState<number>(0);
  const [keywords, setKeywords] = useState<string>('{}');
  const [warehouseName, setWarehouseName] = useState<string>();
  const [productInfo, setProductInfo] = useState<{
    name: string;
    sku: string;
  }>({
    name: '',
    sku: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[Date, Date]>(getDateRange());
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const { id: productId } = useParams();
  const query = useQuery();
  const warehouseId = query.get('warehouse');

  const [startDate, endDate] = dateRange;

  const getHistories = async (params: {
    productId: string;
    warehouseId?: string;
    startDate?: Date;
    endDate?: Date;
    name?: string;
    orderType?: string;
  }) => {
    const { productId, warehouseId, startDate, endDate, name, orderType } = params;
    if (warehouseId) {
      const inventoryHistoryResponse = await getInventoryHistories(productId, {
        warehouseId,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        name,
        orderType,
      });

      return inventoryHistoryResponse;
    }

    const inventoryHistoryResponse = await getInventoryHistories(productId, {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      name,
      orderType,
    });
    return inventoryHistoryResponse;
  };

  const fetchDataOnMountCallback = useCallback(async () => {
    setIsLoading(true);

    try {
      const productResponse = await getVariant(productId!);
      setProductInfo({ name: productResponse.productVariantName, sku: productResponse.sku });

      if (warehouseId) {
        const warehouseResponse = await getWarehouse(warehouseId);
        const inventoryHistoryResponse = await getHistories({
          productId: productId!,
          warehouseId: warehouseId ?? undefined,
          startDate: startDate,
          endDate: endDate,
        });
        setKeywords(inventoryHistoryResponse.keywords);
        setInventoryHistory(inventoryHistoryResponse.data);
        setWarehouseName(warehouseResponse.name);
      }

      if (!warehouseId) {
        const inventoryHistoryResponse = await getHistories({
          productId: productId!,
          startDate: startDate,
          endDate: endDate,
        });
        setInventoryHistory(inventoryHistoryResponse.data);
        setKeywords(inventoryHistoryResponse.keywords);
      }
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  useEffect(() => {
    fetchDataOnMountCallback();
  }, [fetchDataOnMountCallback]);

  useEffect(() => {
    (async () => {
      try {
        const totalStockCount = await calculateInventoryHistory(
          productId!,
          warehouseId ?? undefined
        );
        setTotalStockCount(totalStockCount);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    })();
  }, [productId, warehouseId]);

  const renderTitle = (title: string) => (
    <div style={{ fontWeight: 'bold' }}>
      <Text style={{ fontSize: '1rem' }}>{title}</Text>
    </div>
  );

  const renderItem = (id: string, title: string) => ({
    value: title,
    label: (
      <div key={id}>
        <Text style={{ marginLeft: '1rem' }}>{title}</Text>
      </div>
    ),
  });

  const getOptionGroup = (options: string[]) => {
    const optionGroup: IOptionGroup[] = [];

    for (let [idx, value] of options.entries()) {
      if (!value) {
        continue;
      }
      optionGroup.push(renderItem(`${idx}`, value));
    }

    return optionGroup;
  };

  const getOptions = () => {
    const parsedKeywords: SearchKeywords = JSON.parse(keywords);

    let nameOptionGroup: IOptionGroup[] = [];
    let orderTypeOptionGroup: IOptionGroup[] = [];

    if (Object.keys(parsedKeywords).length) {
      nameOptionGroup = getOptionGroup(parsedKeywords.name);
      orderTypeOptionGroup = getOptionGroup(parsedKeywords.orderType);
    }

    return [
      {
        label: renderTitle('Name'),
        options: nameOptionGroup,
      },

      {
        label: renderTitle('Order Type'),
        options: orderTypeOptionGroup,
      },
    ];
  };

  const onSelectOption = async (searchText: string) => {
    setIsLoading(true);

    const orderTypes: string[] = Object.values(ESourceTransactionType);
    const query: {
      startDate?: Date;
      endDate?: Date;
      name?: string;
      orderType?: string;
    } = {
      startDate,
      endDate,
      name: undefined,
      orderType: undefined,
    };
    searchText.split(', ').forEach((text) => {
      if (orderTypes.includes(text)) {
        query.orderType = text;
      } else {
        query.name = text;
      }
    });

    const inventoryHistoryResponse = await getHistories({
      productId: productId!,
      warehouseId: warehouseId ?? undefined,
      ...query,
    });
    setKeywords(inventoryHistoryResponse.keywords);
    setInventoryHistory(inventoryHistoryResponse.data);

    setIsLoading(false);
  };

  const title = `${warehouseName ?? 'All'} > ${productInfo.name ?? ''} (${productInfo.sku ?? ''
    }) > Available Stock: ${totalStockCount}`;

  const onTableChange: TableProps<IInventoryHistory>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  return (
    <Show title={title} isLoading={isLoading} headerProps={{ extra: null }}>
      <MetaData />
      <section className={styles['filter-section']}>
        <CustomDatePicker
          className={styles['filter-section__datePicker']}
          value={dateRange}
          onChange={(values) => {
            if (values && values[0] && values[1]) {
              setDateRange([new Date(values[0]), new Date(values[1])]);
            }
          }}
        />
        <Search
          placeholder="Search by Client, Supplier, Order Type"
          size="large"
          className={styles['filter-section__search']}
          options={getOptions()}
          onSelectOption={onSelectOption}
          onReset={fetchDataOnMountCallback}
        />
      </section>

      <Table
        dataSource={inventoryHistory}
        rowKey={(history, idx) => `${history.orderId}-${idx}`}
        scroll={{ x: true }}
        pagination={{
          current: page,
          pageSize: limit,
          position: ['bottomCenter'],
        }}
        onChange={onTableChange}
        bordered
      >
        <Table.Column<IInventoryHistory>
          dataIndex="date"
          title="Date"
          render={(_, record): React.ReactNode => {
            const date = new Date(record.date).toLocaleDateString('en-GB');
            return <Text>{date}</Text>;
          }}
        />
        <Table.Column dataIndex="orderType" title="Order Type" />
        <Table.Column dataIndex="orderId" title="Order ID" />
        <Table.Column dataIndex="name" title="Client / Supplier / User" />
        <Table.Column dataIndex="warehouse" title="Warehouse Name" />
        <Table.Column dataIndex="unitPrice" title="Unit Price" />
        <Table.Column dataIndex="quantity" title="Quantity" />
        <Table.Column<IInventoryHistory>
          dataIndex="amount"
          title="Amount"
          render={(_, record) => formatCurrency(record.amount)}
        />
        <Table.Column dataIndex="balanceStock" title="Balance Stock" />
      </Table>
    </Show>
  );
};
